import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { catchError, map, of } from 'rxjs';
import { JobApplicationServiceService } from '../../all-applications/service/job-application-service.service';

interface B2bSessionReportQuestionEvaluation {
  question?: string;
  questionVideoUrl?: string | null;
  explainVideoUrl?: string | null;
  videoUrl?: string | null;
  additionalVideoUrl?: string | null;
  score?: number | null;
  feedback?: string | null;
}

interface B2bSessionReportResponse {
  sessionId?: string;
  applicationId?: string;
  candidateName?: string | null;
  candidateEmail?: string | null;
  interviewDate?: string | null;
  sessionStatus?: string | null;
  evaluationStatus?: string | null;
  overallScore?: number | null;
  technicalSkillsScore?: number | null;
  confidenceScore?: number | null;
  languageProficiencyScore?: number | null;
  overallFitSummary?: string | null;
  keyStrengths?: string | null;
  keyConcerns?: string | null;
  // NOTE: backend keys — these replace the old
  // roleKnowledgeFeedback / communicationFeedback names.
  technicalSkillsFeedback?: string | null;
  languageProficiencyFeedback?: string | null;
  confidenceFeedback?: string | null;
  aiRecommendation?: string | null;
  reportUrl?: string | null;
  questionEvaluations?: B2bSessionReportQuestionEvaluation[] | null;
}

interface QuestionCard {
  text: string;
  answer: string;
  score: string;
  videoUrl: string;
  additionalVideoUrl: string;
  questionVideoUrl: string;
  explainVideoUrl: string;
  downloadingMain: boolean;
  downloadingAdditional: boolean;
}

@Component({
  selector: 'app-vr-interview-result',
  templateUrl: './vr-interview-result.component.html',
  styleUrls: ['./vr-interview-result.component.css'],
})
export class VrInterviewResultComponent implements OnInit, OnDestroy {
  private readonly baseUrl = environment.apiUrl;
  language = 'en';
  isLoading = false;
  sessionId = '';
  report: B2bSessionReportResponse | null = null;
  pdfUrl = '';

  videoStates: {
    [key: string]: {
      isLoading: boolean;
      isPlaying: boolean;
      secureUrl?: string;
      error?: string;
    };
  } = {};

  get isRTL(): boolean {
    return this.language?.toLowerCase() === 'ar';
  }

  readonly staticText = {
    en: {
      interviewDetails: 'Interview Results',
      welcomeHeading: 'Welcome to your Interview Result.',
      candidateIdentifier: 'Candidate Name',
      position: 'Position',
      interviewDate: 'Interview Date',
      executiveSummary: 'Executive Summary',
      overallScore: 'Overall Interview Score',
      recommendation: 'AI Recommendation',
      performanceBreakdown: 'Performance Breakdown',
      questionReview: 'Question Review',
      aiInsights: 'AI Insights',
      question: 'Question',
      aiAnalysis: 'AI Analysis',
      aiMatchScore: 'AI Match Score',
      keyStrengths: 'Key Strengths',
      keyConcerns: 'Key Concerns',
      overallFitSummary: 'Overall Fit Summary',
      noData: '-',
      internalNotes: 'Internal notes',
      addInternalNote: 'Add internal note',
      deleteNote: 'Delete note',
      shortlistCandidate: 'Shortlist Candidate',
      rejectCandidate: 'Reject Candidate',
      interviewVideos: 'Interview report',
      downloadReport: 'Download Report',
      downloadVideo: 'Download Video',
      downloadAdditionalVideo: 'Download Additional Video',
      downloading: 'Downloading...',
      noVideos: 'No report content available',
      noRecording: '-',
      clickToPlay: 'Open report',
      loadingVideo: 'Loading report...',
      clickToReplay: 'Open report again',
      retry: 'Retry',
      answer: 'AI Feedback',
      additional: 'Additional',
      noSessionData: 'No session data available.',
      na: '-',
      candidateEmail: 'Candidate Email',
      sessionStatus: 'Session Status',
      evaluationStatus: 'Evaluation Status',
      technicalSkillsScore: 'Technical Skills',
      confidenceScore: 'Confidence',
      languageProficiencyScore: 'Language Proficiency',
      summary: 'Summary',
      positiveNotes: 'Positive Notes',
      negativeNotes: 'Negative Notes',
      improvementSuggestions: 'Improvement Suggestions',
      reportLink: 'Report Link',
      questionAudio: 'Question Audio',
      explanationAudio: 'Explanation Audio',
      candidateAnswer: 'Candidate Answer',
      notesPlaceholder: 'Write an internal note about this candidate...',
      updating: 'Updating...',
      accepted: 'Accepted',
      rejected: 'Rejected',
      pending: 'Pending Decision',
      currentStatus: 'Current Status',
    },
    ar: {
      interviewDetails: 'نتائج المقابلة',
      welcomeHeading: 'مرحباً بك في نتيجة المقابلة.',
      candidateIdentifier: 'اسم المرشح',
      position: 'المنصب',
      interviewDate: 'تاريخ المقابلة',
      executiveSummary: 'الملخص التنفيذي',
      overallScore: 'إجمالي درجة المقابلة',
      recommendation: 'توصية الذكاء الاصطناعي',
      performanceBreakdown: 'تفاصيل الأداء',
      questionReview: 'مراجعة الأسئلة',
      aiInsights: 'رؤى الذكاء الاصطناعي',
      question: 'السؤال',
      aiAnalysis: 'تحليل الذكاء الاصطناعي',
      aiMatchScore: 'نتيجة التطابق',
      keyStrengths: 'نقاط القوة الرئيسية',
      keyConcerns: 'المخاوف الرئيسية',
      overallFitSummary: 'ملخص الملاءمة العام',
      noData: '-',
      internalNotes: 'ملاحظات داخلية',
      addInternalNote: 'إضافة ملاحظة داخلية',
      deleteNote: 'حذف الملاحظة',
      shortlistCandidate: 'إدراج في القائمة المختصرة',
      rejectCandidate: 'رفض المرشح',
      interviewVideos: 'تقرير المقابلة',
      downloadReport: 'تحميل التقرير',
      downloadVideo: 'تحميل الفيديو',
      downloadAdditionalVideo: 'تحميل الفيديو الإضافي',
      downloading: 'جارٍ التحميل...',
      noVideos: 'لا يوجد محتوى تقرير متاح',
      noRecording: '-',
      clickToPlay: 'فتح التقرير',
      loadingVideo: 'جارٍ تحميل التقرير...',
      clickToReplay: 'فتح التقرير مرة أخرى',
      retry: 'إعادة المحاولة',
      answer: 'تقييم الذكاء الاصطناعي',
      additional: 'إضافي',
      noSessionData: 'لا توجد بيانات للجلسة.',
      na: '-',
      candidateEmail: 'البريد الإلكتروني للمرشح',
      sessionStatus: 'حالة الجلسة',
      evaluationStatus: 'حالة التقييم',
      technicalSkillsScore: 'المهارات التقنية',
      confidenceScore: 'الثقة',
      languageProficiencyScore: 'إتقان اللغة',
      summary: 'الملخص',
      positiveNotes: 'الملاحظات الإيجابية',
      negativeNotes: 'الملاحظات السلبية',
      improvementSuggestions: 'اقتراحات التحسين',
      reportLink: 'رابط التقرير',
      questionAudio: 'صوت السؤال',
      explanationAudio: 'صوت الشرح',
      candidateAnswer: 'إجابة المرشح',
      notesPlaceholder: 'اكتب ملاحظة داخلية عن هذا المرشح...',
      updating: 'جارٍ التحديث...',
      accepted: 'مقبول',
      rejected: 'مرفوض',
      pending: 'قيد الانتظار',
      currentStatus: 'الحالة الحالية',
    },
  };

  get t() {
    return this.isRTL ? this.staticText.ar : this.staticText.en;
  }

  candidateInfo = {
    identifier: '-',
    email: '-',
    position: '-',
    interviewDate: '-',
    sessionStatus: '-',
    evaluationStatus: '-',
  };

  executiveSummary = {
    overallScore: '-',
    recommendation: '-',
  };

  performanceBreakdown = [
    {
      title: 'Technical Skills',
      titleAr: 'المهارات التقنية',
      description: '-',
      score: '-',
    },
    { title: 'Confidence', titleAr: 'الثقة', description: '-', score: '-' },
    {
      title: 'Language Proficiency',
      titleAr: 'إتقان اللغة',
      description: '-',
      score: '-',
    },
  ];

  questions: QuestionCard[] = [];
  applicationId: string | null = null;

  /** HR internal note typed for this application before submitting a status change */
  notes: string = '';

  /** Local copy of the application status/notes, kept in sync with backend */
  applicationDetails: { status: string; notes: string } = {
    status: '',
    notes: '',
  };

  isUpdatingStatus = false;
  statusError = '';
  statusSuccess = '';

  aiInsights = {
    keyStrengths: '-',
    keyConcerns: '-',
    overallAssessment: '-',
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private applicationService: JobApplicationServiceService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.sessionId = params.get('id') || '';

      if (this.sessionId) {
        this.loadReport();
      }
    });
  }

  onRejectApplication(): void {
    this.updateStatus('REJECTED');
  }

  onAccept(): void {
    this.updateStatus('ACCEPTED');
  }

  /**
   * Sends both status and notes to the backend.
   * Previously only `status` was passed here, so any text the HR typed in
   * the internal-notes textarea was silently dropped — `notes` is now read
   * from the component's `notes` field (bound to the textarea) and included
   * in the payload. The service already strips empty/undefined values, so
   * an empty note simply won't be sent.
   */
  private updateStatus(status: string): void {
    if (!this.applicationId || this.isUpdatingStatus) return;

    this.isUpdatingStatus = true;
    this.statusError = '';
    this.statusSuccess = '';

    const trimmedNotes = this.notes?.trim();

    const payload: { status: string; notes?: string } = {
      status,
      notes: trimmedNotes ? trimmedNotes : undefined,
    };

    this.applicationService
      .updateApplicationStatus(payload, this.applicationId)
      .subscribe({
        next: () => {
          this.applicationDetails.status = status;
          if (trimmedNotes) {
            this.applicationDetails.notes = trimmedNotes;
          }
          this.statusSuccess = `Status updated to ${status}`;
          this.isUpdatingStatus = false;
          // Clear success message after 3s
          setTimeout(() => (this.statusSuccess = ''), 3000);
        },
        error: (err) => {
          console.error('Failed to update status:', err);
          this.statusError = 'Failed to update status. Please try again.';
          this.isUpdatingStatus = false;
          setTimeout(() => (this.statusError = ''), 4000);
        },
      });
  }

  private loadReport(): void {
    this.isLoading = true;
    this.http
      .get<B2bSessionReportResponse>(
        `${this.baseUrl}api/b2b/interview/company/session/${this.sessionId}/report`,
      )
      .pipe(
        catchError((error) => {
          console.error('Failed to load report:', error);
          this.isLoading = false;
          return of(null);
        }),
      )
      .subscribe((report) => {
        this.report = report;
        if (report) {
          this.populateReport(report);
        }
        this.isLoading = false;
      });
  }

  private populateReport(report: B2bSessionReportResponse): void {
    this.candidateInfo = {
      identifier: this.formatValue(report.candidateName),
      email: this.formatValue(report.candidateEmail),
      position: '-',
      interviewDate: this.formatDate(report.interviewDate),
      sessionStatus: this.formatValue(report.sessionStatus),
      evaluationStatus: this.formatValue(report.evaluationStatus),
    };

    this.executiveSummary = {
      overallScore: this.formatNumber(report.overallScore),
      recommendation: this.formatValue(report.aiRecommendation),
    };

    const technical = this.formatNumber(report.technicalSkillsScore);
    const confidence = this.formatNumber(report.confidenceScore);
    const language = this.formatNumber(report.languageProficiencyScore);
    this.applicationId = report.applicationId ?? '';

    // Once we know the applicationId, fetch the current status/notes so the
    // notes textarea and status buttons reflect what's already saved.
    if (this.applicationId) {
      this.applicationService
        .getApplicationDetails(this.applicationId)
        .pipe(
          catchError((error) => {
            console.error('Failed to load application details:', error);
            return of(null);
          }),
        )
        .subscribe((app) => {
          if (app) {
            this.applicationDetails = {
              status: app.status ?? '',
              notes: app.notes ?? '',
            };
            this.notes = app.notes ?? '';
          }
        });
    }

    this.performanceBreakdown = [
      {
        title: 'Technical Skills',
        titleAr: 'المهارات التقنية',
        description: this.formatValue(report.technicalSkillsFeedback),
        score: technical,
      },
      {
        title: 'Confidence',
        titleAr: 'الثقة',
        description: this.formatValue(report.confidenceFeedback),
        score: confidence,
      },
      {
        title: 'Language Proficiency',
        titleAr: 'إتقان اللغة',
        description: this.formatValue(report.languageProficiencyFeedback),
        score: language,
      },
    ];

    this.questions = (report.questionEvaluations ?? []).map((q) => ({
      text: this.formatValue(q.question),
      answer: this.formatValue(q.feedback),
      score: this.formatNumber(q.score),
      videoUrl: this.buildSecureUrl(q.videoUrl),
      additionalVideoUrl: this.buildSecureUrl(q.additionalVideoUrl),
      questionVideoUrl: this.buildSecureUrl(q.questionVideoUrl),
      explainVideoUrl: this.buildSecureUrl(q.explainVideoUrl),
      downloadingMain: false,
      downloadingAdditional: false,
    }));

    this.questions.forEach((q, index) => {
      if (q.videoUrl)
        this.videoStates[`main_${index}`] = {
          isLoading: false,
          isPlaying: false,
        };
      if (q.questionVideoUrl)
        this.videoStates[`question_${index}`] = {
          isLoading: false,
          isPlaying: false,
        };
      if (q.explainVideoUrl)
        this.videoStates[`explain_${index}`] = {
          isLoading: false,
          isPlaying: false,
        };
      if (q.additionalVideoUrl)
        this.videoStates[`additional_${index}`] = {
          isLoading: false,
          isPlaying: false,
        };
    });

    this.aiInsights = {
      keyStrengths: this.formatValue(report.keyStrengths),
      keyConcerns: this.formatValue(report.keyConcerns),
      overallAssessment: this.formatValue(report.overallFitSummary),
    };

    this.pdfUrl = this.buildSecureUrl(report.reportUrl);
  }

  private formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  }

  private formatNumber(value: number | null | undefined): string {
    return typeof value === 'number' ? value.toString() : '-';
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? '-'
      : parsed.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        });
  }

  private buildSecureUrl(relativeUrl: string | null | undefined): string {
    if (!relativeUrl) return '';
    let fixedUrl = relativeUrl.replace(/\\/g, '/');

    if (fixedUrl.startsWith('/api/')) {
      fixedUrl = fixedUrl.substring(5);
    } else if (fixedUrl.startsWith('/')) {
      fixedUrl = fixedUrl.substring(1);
    }

    if (fixedUrl.startsWith('http://') || fixedUrl.startsWith('https://')) {
      return fixedUrl;
    }

    return `${this.baseUrl}${fixedUrl}`;
  }

  getPerformanceTitle(item: any): string {
    return this.isRTL ? item.titleAr : item.title;
  }

  getCircumference(): number {
    return 2 * Math.PI * 22;
  }

  getStrokeDashoffset(score: number | string): number {
    const numericScore = typeof score === 'number' ? score : Number(score);
    if (!Number.isFinite(numericScore)) return this.getCircumference();
    const circumference = this.getCircumference();
    return (
      circumference -
      (Math.max(0, Math.min(100, numericScore)) / 100) * circumference
    );
  }

  getMatchScoreColor(score: number | string): string {
    const numericScore = typeof score === 'number' ? score : Number(score);
    if (!Number.isFinite(numericScore)) return '#64748b';
    if (numericScore >= 80) return '#16a34a';
    if (numericScore >= 60) return '#d97706';
    return '#dc2626';
  }

  /** True once the backend has recorded a final decision for this application */
  get isAccepted(): boolean {
    return this.applicationDetails.status === 'ACCEPTED';
  }

  get isRejected(): boolean {
    return this.applicationDetails.status === 'REJECTED';
  }

  getStatusBadgeClass(): string {
    if (this.isAccepted) return 'status-badge status-accepted';
    if (this.isRejected) return 'status-badge status-rejected';
    return 'status-badge status-pending';
  }

  getStatusLabel(): string {
    if (this.isAccepted) return this.t.accepted;
    if (this.isRejected) return this.t.rejected;
    return this.t.pending;
  }

  private getAuthHeaders(): HttpHeaders | undefined {
    const token = localStorage.getItem('accessToken');
    return token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;
  }

  private triggerBlobDownload(blob: Blob, filename: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);
  }

  downloadPDF(): void {
    if (!this.pdfUrl) return;

    this.http
      .get(this.pdfUrl, {
        headers: this.getAuthHeaders(),
        responseType: 'blob',
      })
      .pipe(
        map((blob: Blob) => URL.createObjectURL(blob)),
        catchError((error) => {
          console.error('Failed to load secured report PDF:', error);
          return of('');
        }),
      )
      .subscribe((blobUrl) => {
        if (blobUrl) {
          window.open(blobUrl, '_blank');
        }
      });
  }

  downloadQuestionVideo(question: QuestionCard, index: number): void {
    if (!question.videoUrl || question.downloadingMain) return;
    question.downloadingMain = true;

    this.http
      .get(question.videoUrl, {
        headers: this.getAuthHeaders(),
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          console.error(
            `Failed to download video for question ${index + 1}:`,
            error,
          );
          return of(null);
        }),
      )
      .subscribe((blob) => {
        if (blob) {
          this.triggerBlobDownload(blob, `question-${index + 1}-video.webm`);
        }
        question.downloadingMain = false;
      });
  }

  downloadAdditionalVideo(question: QuestionCard, index: number): void {
    if (!question.additionalVideoUrl || question.downloadingAdditional) return;
    question.downloadingAdditional = true;

    this.http
      .get(question.additionalVideoUrl, {
        headers: this.getAuthHeaders(),
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          console.error(
            `Failed to download additional video for question ${index + 1}:`,
            error,
          );
          return of(null);
        }),
      )
      .subscribe((blob) => {
        if (blob) {
          this.triggerBlobDownload(
            blob,
            `question-${index + 1}-additional-video.webm`,
          );
        }
        question.downloadingAdditional = false;
      });
  }

  shortlistCandidate(): void {}

  rejectCandidate(): void {}

  playVideo(videoUrl: string | null, key: string) {
    if (!videoUrl) return;
    if (this.videoStates[key]?.secureUrl && this.videoStates[key]?.isPlaying)
      return;
    if (this.videoStates[key]?.secureUrl && !this.videoStates[key]?.isPlaying) {
      this.videoStates[key] = {
        ...this.videoStates[key],
        isPlaying: true,
        isLoading: false,
        error: undefined,
      };
      return;
    }

    this.videoStates[key] = { isLoading: true, isPlaying: false };
    this.http
      .get(videoUrl, { headers: this.getAuthHeaders(), responseType: 'blob' })
      .subscribe({
        next: (blob: Blob) => {
          const blobUrl = URL.createObjectURL(blob);
          this.videoStates[key] = {
            isLoading: false,
            isPlaying: true,
            secureUrl: blobUrl,
          };
        },
        error: () => {
          this.videoStates[key] = {
            isLoading: false,
            isPlaying: false,
            error: 'Failed to load video. Please try again.',
          };
        },
      });
  }

  onVideoEnded(key: string) {
    if (this.videoStates[key])
      this.videoStates[key] = { ...this.videoStates[key], isPlaying: false };
  }

  onVideoLoaded(key: string, event?: any) {}

  onVideoError(key: string, event?: any) {
    let errorMessage = 'Video playback error';
    if (event?.target?.error) {
      const messages: { [k: number]: string } = {
        1: 'Video loading was aborted',
        2: 'Network error while loading video',
        3: 'Video format not supported or corrupted',
        4: 'Video format not supported by browser',
      };
      errorMessage = messages[event.target.error.code] || errorMessage;
    }
    if (this.videoStates[key]) {
      this.videoStates[key] = {
        ...this.videoStates[key],
        isLoading: false,
        isPlaying: false,
        error: errorMessage,
      };
    }
  }

  retryVideo(key: string) {
    if (this.videoStates[key]?.secureUrl)
      URL.revokeObjectURL(this.videoStates[key].secureUrl!);
    this.videoStates[key] = { isLoading: false, isPlaying: false };
    const parts = key.split('_');
    const type = parts[0];
    const index = parseInt(parts[1], 10);
    const q = this.questions[index];

    let url = '';
    if (type === 'main') url = q.videoUrl;
    else if (type === 'question') url = q.questionVideoUrl;
    else if (type === 'explain') url = q.explainVideoUrl;
    else if (type === 'additional') url = q.additionalVideoUrl;

    this.playVideo(url, key);
  }

  private cleanupVideoStates() {
    Object.values(this.videoStates).forEach((state) => {
      if (state.secureUrl) URL.revokeObjectURL(state.secureUrl);
    });
  }

  ngOnDestroy() {
    this.cleanupVideoStates();
  }
}
